import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ClingSprite } from './ClingSprite';
import type { ClingGameState } from '../../lib/melody/clingCantaEngine';
import type { ExploreGameState } from '../../lib/melody/clingExploreEngine';
import type { ClingPlayMode, SingPitchId } from '../../lib/melody/clingCantaConfig';
import {
  CLING_LANE_HEIGHT,
  CLING_NOTE_HEIGHT,
  clingRollContentHeight,
  pitchLaneCenterY,
  pitchLaneTop,
} from '../../lib/melody/clingCantaPitch';
import {
  clingScreenX,
  exploreLaneBandHeight,
  exploreLaneBandTop,
  flyZoneBounds,
} from '../../lib/melody/clingWorldConfig';
import {
  exploreCollectedCount,
  exploreDisplayPitchY,
  introPlayedCount,
} from '../../lib/melody/clingExploreEngine';
import { noteVisual } from '../../lib/melody/clingCantaEngine';
import { clingDisplaySize } from '../../lib/melody/clingSprite';
import type { ClingVisualSettings } from '../../lib/melody/clingVisualConfig';

const EXPLORE_NOTE_WIDTH = 52;

export interface ClingWorldStageHandle {
  syncExploreMotion(state: ExploreGameState): void;
}

interface ClingWorldStageProps {
  mode: ClingPlayMode;
  cantoGame?: ClingGameState;
  exploreGame?: ExploreGameState;
  clingFrame: number;
  clingFilter?: string;
  visual?: ClingVisualSettings;
  isSinging: boolean;
  volume: number;
  micActive: boolean;
  detectedPitch: string | null;
  onSizeChange: (w: number, h: number) => void;
}

function cantoLaneLayout(lanes: SingPitchId[], stageH: number) {
  const contentH = clingRollContentHeight(lanes.length);
  const fly = flyZoneBounds(stageH);
  const offset = fly.top + (fly.bottom - fly.top - contentH) / 2;
  return { offset };
}

function ExploreNote({
  item,
  displayLanes,
  stageH,
  scrollX,
  stageW,
  fixedX,
}: {
  item: ExploreGameState['items'][number] | { pitchId: SingPitchId; fill: string; color: string; label: string };
  displayLanes: SingPitchId[];
  stageH: number;
  scrollX: number;
  stageW: number;
  fixedX?: number;
}) {
  const screenX = fixedX != null ? fixedX : ('worldX' in item ? item.worldX - scrollX : 0);
  if (fixedX == null && 'worldX' in item) {
    if (screenX > stageW + 64 || screenX < -64) return null;
  }
  const cy = exploreDisplayPitchY(item.pitchId, displayLanes, stageH);
  const left = fixedX != null ? fixedX : ('worldX' in item ? item.worldX - EXPLORE_NOTE_WIDTH / 2 : 0);
  return (
    <div
      className="cling-canta-note cling-canta-note--world cling-canta-note--collect"
      style={{
        left,
        width: EXPLORE_NOTE_WIDTH,
        top: cy - CLING_NOTE_HEIGHT / 2,
        height: CLING_NOTE_HEIGHT,
        ['--note-fill' as string]: item.fill,
        ['--note-color' as string]: item.color,
      }}
    >
      <span className="cling-canta-note-head" />
      <span className="cling-canta-note-label">{item.label}</span>
    </div>
  );
}

function ExplorePitchLanes({
  displayLanes,
  activeLanes,
  stageH,
}: {
  displayLanes: SingPitchId[];
  activeLanes: SingPitchId[];
  stageH: number;
}) {
  const bandH = exploreLaneBandHeight(stageH, displayLanes.length);
  const activeSet = new Set(activeLanes);
  return (
    <>
      {displayLanes.map((pitchId) => {
        const active = activeSet.has(pitchId);
        const visual = noteVisual(pitchId);
        return (
          <div
            key={pitchId}
            className={`cling-canta-lane cling-canta-lane--world ${active ? 'cling-canta-lane--explore' : 'cling-canta-lane--explore-inactive'}`}
            style={{
              top: exploreLaneBandTop(pitchId, displayLanes, stageH),
              height: bandH,
              ['--lane-fill' as string]: visual.fill,
              ['--lane-color' as string]: visual.color,
            }}
          >
            <span className="cling-canta-lane-label">{pitchId.replace(/(\d)/, ' $1')}</span>
          </div>
        );
      })}
    </>
  );
}

function CantoPitchLanes({
  lanes,
  stageH,
  guideAlpha,
}: {
  lanes: SingPitchId[];
  stageH: number;
  guideAlpha: number;
}) {
  const { offset } = cantoLaneLayout(lanes, stageH);
  return (
    <>
      {lanes.map((pitchId, i) => (
        <div
          key={pitchId}
          className={`cling-canta-lane cling-canta-lane--world cling-canta-lane--guide ${i % 2 === 0 ? 'cling-canta-lane--alt' : ''}`}
          style={{
            top: offset + pitchLaneTop(pitchId, lanes),
            height: CLING_LANE_HEIGHT,
            opacity: guideAlpha,
          }}
        >
          <span className="cling-canta-lane-label">{pitchId.replace(/(\d)/, ' $1')}</span>
        </div>
      ))}
    </>
  );
}

export const ClingWorldStage = forwardRef<ClingWorldStageHandle, ClingWorldStageProps>(function ClingWorldStage(
  {
    mode,
    cantoGame,
    exploreGame,
    clingFrame,
    clingFilter,
    visual,
    isSinging,
    volume,
    micActive,
    detectedPitch,
    onSizeChange,
  },
  ref
) {
  const stageRef = useRef<HTMLDivElement>(null);
  const worldLayerRef = useRef<HTMLDivElement>(null);
  const clingWrapRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ w: 640, h: 360 });
  const clingSize = clingDisplaySize();

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setStageSize({ w: width, h: height });
      onSizeChange(width, height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSizeChange]);

  const stageW = stageSize.w;
  const stageH = stageSize.h;
  const cx = clingScreenX(stageW);

  const scrollX =
    mode === 'exploracion' ? (exploreGame?.scrollX ?? 0) : (cantoGame?.scrollX ?? 0);

  const displayLanes = exploreGame?.level.displayLanes ?? [];
  const activeLanes = exploreGame?.level.activeLanes ?? [];

  const clingY = (() => {
    if (mode === 'exploracion') {
      if (!exploreGame) return stageH * 0.5;
      if (exploreGame.clingY > 0) return exploreGame.clingY;
      const mid = activeLanes[Math.floor(activeLanes.length / 2)] ?? 'Mi4';
      return exploreDisplayPitchY(mid, displayLanes, stageH);
    }
    if (!cantoGame) return stageH * 0.5;
    const contentH = clingRollContentHeight(cantoGame.exercise.lanes.length);
    const fly = flyZoneBounds(stageH);
    const t = Math.min(1, Math.max(0, cantoGame.clingY / Math.max(1, contentH)));
    return fly.top + t * (fly.bottom - fly.top);
  })();

  useImperativeHandle(
    ref,
    () => ({
      syncExploreMotion(state) {
        if (worldLayerRef.current) {
          worldLayerRef.current.style.transform = `translate3d(${-state.scrollX}px, 0, 0)`;
        }
        if (clingWrapRef.current) {
          clingWrapRef.current.style.top = `${state.clingY - clingSize.height * 0.5}px`;
        }
      },
    }),
    [clingSize.height]
  );

  const feedback =
    mode === 'exploracion'
      ? exploreGame?.feedback === 'collect'
        ? 'hit'
        : exploreGame?.feedback === 'miss'
          ? 'miss'
          : null
      : cantoGame?.feedback === 'hit'
        ? 'hit'
        : cantoGame?.feedback === 'miss'
          ? 'miss'
          : null;

  const countdown =
    mode === 'exploracion' ? exploreGame?.countdownBeat : cantoGame?.countdownBeat;

  const phase = mode === 'exploracion' ? exploreGame?.phase : cantoGame?.phase;

  return (
    <div className="cling-canta-stage cling-canta-stage--playing flex flex-col flex-1 min-h-0 gap-1">
      <div className="cling-canta-hud shrink-0 flex items-center justify-between gap-2 px-1 flex-wrap">
        {mode === 'exploracion' && exploreGame && (
          <div className="cling-canta-hud-pill">
            {exploreGame.phase === 'introDemo' ? (
              <>
                <span>Escucha</span>
                <strong>
                  {introPlayedCount(exploreGame)}/{exploreGame.introNotes.length}
                </strong>
              </>
            ) : exploreGame.phase === 'countdown' ? (
              <>
                <span>Preparados</span>
                <strong>{exploreGame.countdownBeat || '¡Ya!'}</strong>
              </>
            ) : (
              <>
                <span>Recogidos</span>
                <strong>
                  {exploreCollectedCount(exploreGame)}/{exploreGame.items.length}
                </strong>
              </>
            )}
          </div>
        )}
        {mode === 'canto' && cantoGame && (
          <>
            <div className="cling-canta-hud-pill">
              <span>Puntos</span>
              <strong>{cantoGame.score}</strong>
            </div>
            <div className="cling-canta-hud-pill">
              <span>Combo</span>
              <strong>{cantoGame.combo > 0 ? `×${cantoGame.combo}` : '—'}</strong>
            </div>
          </>
        )}
        <div className="cling-canta-hud-pill cling-canta-hud-pill--mode">
          {mode === 'exploracion' ? '🗺️ Exploración' : '🎤 Canto'}
        </div>
        <div
          className={`cling-canta-mic-meter ${micActive || isSinging ? 'cling-canta-mic-meter--on' : ''}`}
          style={{ ['--vol' as string]: `${Math.round(Math.max(volume, 0.02) * 100)}%` }}
          title={detectedPitch ? `Detectado: ${detectedPitch}` : micActive ? 'Audio detectado' : 'Silencio'}
        >
          {detectedPitch ?? '🎤'}
        </div>
      </div>

      <div
        ref={stageRef}
        className={`cling-world-stage cling-canta-roll flex-1 min-h-0 relative overflow-hidden rounded-xl ${
          feedback === 'hit' ? 'cling-canta-roll--hit' : feedback === 'miss' ? 'cling-canta-roll--miss' : ''
        } ${visual?.brightnessEffects ? 'cling-world-stage--glow' : ''}`}
        style={
          visual?.brightnessEffects
            ? ({ ['--cling-glow' as string]: `${visual.brightnessLevel}%` } as React.CSSProperties)
            : undefined
        }
      >
        {mode === 'exploracion' && exploreGame && (
          <ExplorePitchLanes
            displayLanes={displayLanes}
            activeLanes={activeLanes}
            stageH={stageH}
          />
        )}

        <div
          ref={worldLayerRef}
          className="cling-world-layer"
          style={{ transform: `translate3d(${-scrollX}px, 0, 0)` }}
        >
          {mode === 'exploracion' &&
            exploreGame?.phase === 'play' &&
            exploreGame.items.map((item) => {
              if (item.collected) return null;
              return (
                <ExploreNote
                  key={item.id}
                  item={item}
                  displayLanes={displayLanes}
                  stageH={stageH}
                  scrollX={scrollX}
                  stageW={stageW}
                />
              );
            })}

          {mode === 'exploracion' &&
            exploreGame?.phase === 'introDemo' &&
            exploreGame.introNotes.map((note) => (
              <ExploreNote
                key={note.id}
                item={note}
                displayLanes={displayLanes}
                stageH={stageH}
                scrollX={0}
                stageW={stageW}
                fixedX={note.x}
              />
            ))}

          {mode === 'canto' &&
            cantoGame?.notes.map((note) => {
              const lanes = cantoGame.exercise.lanes;
              const { offset } = cantoLaneLayout(lanes, stageH);
              const cy = offset + pitchLaneCenterY(note.pitchId, lanes);
              return (
                <div
                  key={note.id}
                  className={[
                    'cling-canta-note cling-canta-note--world',
                    note.hit ? 'cling-canta-note--hit' : '',
                    note.missed ? 'cling-canta-note--miss' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    left: note.x + scrollX,
                    width: note.width,
                    top: cy - CLING_NOTE_HEIGHT / 2,
                    height: CLING_NOTE_HEIGHT,
                    ['--note-fill' as string]: note.fill,
                    ['--note-color' as string]: note.color,
                  }}
                >
                  <span className="cling-canta-note-head" />
                  <span className="cling-canta-note-label">{note.label}</span>
                </div>
              );
            })}
        </div>

        {mode === 'canto' &&
          cantoGame &&
          (visual?.pitchGuide ?? false) && (
            <CantoPitchLanes
              lanes={cantoGame.exercise.lanes}
              stageH={stageH}
              guideAlpha={(visual!.pitchGuideLevel / 100) * 0.85}
            />
          )}

        <div className="cling-canta-cling-line cling-canta-cling-line--world" style={{ left: cx }} aria-hidden />

        <div
          ref={clingWrapRef}
          className="cling-canta-cling-wrap cling-canta-cling-wrap--world"
          style={{
            left: cx - clingSize.width * 0.22,
            top: clingY - clingSize.height * 0.5,
          }}
        >
          <ClingSprite frameIndex={clingFrame} singing={isSinging} filter={clingFilter} />
        </div>

        {phase === 'countdown' && countdown != null && countdown > 0 && (
          <div className="cling-canta-countdown" aria-live="polite">
            {countdown}
          </div>
        )}

        {mode === 'canto' && phase === 'demo' && (
          <div className="cling-canta-phase-tag">Escucha…</div>
        )}
        {mode === 'canto' && phase === 'play' && (
          <div className="cling-canta-phase-tag cling-canta-phase-tag--sing">¡Canta!</div>
        )}
        {mode === 'exploracion' && phase === 'introDemo' && (
          <div className="cling-canta-phase-tag">Escucha…</div>
        )}
        {mode === 'exploracion' && phase === 'play' && (
          <div className="cling-canta-phase-tag cling-canta-phase-tag--explore">¡Canta y recoge!</div>
        )}
      </div>
    </div>
  );
});

export default ClingWorldStage;
